import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    Box, Card, Stack, TextField, MenuItem,
    Button, LinearProgress, Typography, Alert, Snackbar,
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
    FormControlLabel, Checkbox, Paper, InputAdornment,
    Tooltip, Fade
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Business as BusinessIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';

const CostCentres = ({ setIsLoggedIn }) => {
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });
    const tenant = localStorage.getItem('tenant') || '';

    // Data States
    const [costCentres, setCostCentres] = useState([]);

    // Dialog States
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        description: '',
        is_active: 1
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const headers = { "X-Tenant": tenant };
            // Although UI is under Payroll, the API was built under SOM in backend
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/som/cost-centres`, { withCredentials: true, headers });

            if (res.data.success) {
                setCostCentres(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setStatusMsg({ open: true, msg: 'Error fetching cost centres', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [tenant]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenDialog = useCallback((item = null) => {
        setEditingItem(item);
        if (item) {
            setFormData({ ...item });
        } else {
            setFormData({
                code: '',
                name: '',
                description: '',
                is_active: 1
            });
        }
        setDialogOpen(true);
    }, []);

    const handleSave = useCallback(async () => {
        if (loading) return;
        
        // Basic Validation
        if (!formData.code || !formData.name) {
            return setStatusMsg({ open: true, msg: 'Code and Name are required', severity: 'warning' });
        }

        setLoading(true);
        try {
            const headers = { "X-Tenant": tenant };
            const url = editingItem
                ? `${process.env.REACT_APP_API_URL1}/som/cost-centres/${editingItem.id}`
                : `${process.env.REACT_APP_API_URL1}/som/cost-centres`;

            const method = editingItem ? 'put' : 'post';
            const response = await axios[method](url, formData, { withCredentials: true, headers });

            if (response.data.success) {
                setStatusMsg({ open: true, msg: `${editingItem ? 'Updated' : 'Created'} successfully`, severity: 'success' });
                setDialogOpen(false);
                fetchData();
            }
        } catch (error) {
            console.error("Error saving:", error);
            setStatusMsg({ open: true, msg: error.response?.data?.message || 'Error saving data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [editingItem, fetchData, formData, loading, tenant]);

    const handleDelete = useCallback(async (id) => {
        if (!window.confirm("Are you sure you want to delete this Cost Centre? This may affect staff allocations and accounting exports.")) return;
        setLoading(true);
        try {
            const headers = { "X-Tenant": tenant };
            const response = await axios.delete(`${process.env.REACT_APP_API_URL1}/som/cost-centres/${id}`, { withCredentials: true, headers });
            if (response.data.success) {
                setStatusMsg({ open: true, msg: 'Deleted successfully', severity: 'success' });
                fetchData();
            }
        } catch (error) {
            console.error("Error deleting:", error);
            setStatusMsg({ open: true, msg: error.response?.data?.message || 'Error deleting cost centre', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [fetchData, tenant]);

    const commonColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
        headerClass: 'premium-grid-header'
    }), []);

    const actionsRenderer = useCallback((params) => (
        <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
            <Tooltip title="Edit">
                <IconButton size="small" sx={{ color: 'primary.main' }} onClick={() => handleOpenDialog(params.data)}>
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={() => handleDelete(params.data.id)}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    ), [handleDelete, handleOpenDialog]);

    const columnDefs = useMemo(() => ([
        { field: "code", headerName: "Code", width: 150, cellStyle: { fontWeight: 'bold', color: 'primary.main' } },
        { field: "name", headerName: "Cost Centre Name", flex: 1, cellStyle: { fontWeight: 600, color: '#1e293b' } },
        { field: "description", headerName: "Description", flex: 1 },
        { field: "is_active", headerName: "Status", width: 150, cellRenderer: p => (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.value ? '#10b981' : '#94a3b8', mr: 1 }} />
                <Typography variant="body2" sx={{ color: p.value ? '#059669' : '#64748b', fontWeight: 600 }}>{p.value ? 'Active' : 'Inactive'}</Typography>
            </Box>
        )},
        { headerName: "Actions", width: 120, cellRenderer: p => actionsRenderer(p) }
    ]), [actionsRenderer]);

    const activeRows = useMemo(() => {
        if (!searchText) return costCentres;
        const s = searchText.toLowerCase();
        return costCentres.filter(item => 
            String(item.code || '').toLowerCase().includes(s) ||
            String(item.name || '').toLowerCase().includes(s) ||
            String(item.description || '').toLowerCase().includes(s)
        );
    }, [costCentres, searchText]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader title="Cost Centres Setup" setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: 4, flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 1200, mx: 'auto', width: '100%' }}>
                <Fade in timeout={500}>
                    <Card sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)', bgcolor: '#fff' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ p: 1.5, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                                    <BusinessIcon sx={{ color: 'primary.main' }} />
                                </Box>
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                                        Cost Centres Setup
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                        Define Cost Centres for staff allocation and accounting integration.
                                    </Typography>
                                </Box>
                            </Box>
                            <Stack direction="row" spacing={1.5}>
                                <TextField
                                    size="small"
                                    placeholder="Quick search..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                        sx: { borderRadius: 2, bgcolor: '#f1f5f9', border: 'none', '& fieldset': { border: 'none' } }
                                    }}
                                />
                                <Tooltip title="Refresh Data">
                                    <IconButton onClick={fetchData} sx={{ bgcolor: '#f1f5f9' }}><RefreshIcon /></IconButton>
                                </Tooltip>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    sx={{ 
                                        bgcolor: 'primary.main', 
                                        borderRadius: 2, 
                                        px: 3, 
                                        textTransform: 'none', 
                                        fontWeight: 700,
                                        boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`,
                                        '&:hover': { bgcolor: 'primary.dark' }
                                    }}
                                    onClick={() => handleOpenDialog()}
                                >
                                    Add New
                                </Button>
                            </Stack>
                        </Stack>
                    </Card>
                </Fade>

                <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flexGrow: 1, p: 1, bgcolor: '#fff' }}>
                        {loading && <LinearProgress sx={{ borderRadius: 1, mb: 1, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' } }} />}
                        <Box className="ag-theme-quartz" sx={{ height: '100%', width: '100%', '& .premium-grid-header': { fontWeight: 800, color: '#475569' } }}>
                            <AgGridReact
                                rowData={activeRows}
                                columnDefs={columnDefs}
                                defaultColDef={commonColDef}
                                rowHeight={52}
                                animateRows={true}
                                pagination={true}
                                paginationPageSize={20}
                                headerHeight={48}
                            />
                        </Box>
                    </Box>
                </Paper>
            </Box>

            <Dialog 
                open={dialogOpen} 
                onClose={() => setDialogOpen(false)} 
                maxWidth="sm" 
                fullWidth
                PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, fontSize: '1.25rem', color: '#0f172a' }}>
                    {editingItem ? 'Update Cost Centre' : 'Create Cost Centre'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}>
                        <TextField
                            fullWidth label="Cost Centre Code" size="small"
                            value={formData.code || ''}
                            onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                            placeholder="e.g. CC001"
                            required
                        />
                        <TextField
                            fullWidth label="Cost Centre Name" size="small"
                            value={formData.name || ''}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g. Sales Department"
                            required
                        />
                        <TextField
                            fullWidth label="Description" size="small"
                            value={formData.description || ''}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Optional details..."
                            multiline
                            rows={3}
                        />

                        <Paper sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #f1f5f9' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={formData.is_active === 1} 
                                        onChange={e => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                                        sx={{ color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                                    />
                                }
                                label={<Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>Active & Available for Allocation</Typography>}
                                sx={{ ml: 0 }}
                            />
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ color: '#64748b', fontWeight: 600, textTransform: 'none' }}>Cancel</Button>
                    <Button 
                        onClick={handleSave} 
                        variant="contained" 
                        sx={{ 
                            bgcolor: 'primary.main', 
                            borderRadius: 2, 
                            px: 3, 
                            fontWeight: 700, 
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'primary.dark' }
                        }}
                    >
                        {editingItem ? 'Confirm Update' : 'Create Record'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={statusMsg.open}
                autoHideDuration={4000}
                onClose={() => setStatusMsg({ ...statusMsg, open: false })}
                TransitionComponent={Fade}
            >
                <Alert 
                    severity={statusMsg.severity} 
                    variant="filled" 
                    sx={{ width: '100%', borderRadius: 3, fontWeight: 600, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                >
                    {statusMsg.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CostCentres;
